import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Switch } from "@progress/kendo-react-inputs";
import {
    Card,
    CardBody, CardSubtitle
} from "@progress/kendo-react-layout";
import { caretAltDownIcon } from "@progress/kendo-svg-icons";
import { ViewModel } from "TypeGen/Order/Images/view-model";
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { useDebouncedCallback } from 'use-debounce';
import { OrderImage } from "../../../TypeGen/Order/Images/order-image";
import { JsonResponse } from "../../../TypeGen/json-response";
import { fetchApi } from "../../../services/api";
import { openWindowPerc } from "../../../services/openWindow";

type RouteComponentParams = {
  orderId: string;
};

const ImageViewer = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { orderId: orderIdParam } = useParams<RouteComponentParams>();
  const [details, setDetails] = useState<ViewModel>();
  const [visibleImages, setVisibleImages] = useState<OrderImage[]>([]);
  const [allImages, setAllImages] = useState<OrderImage[]>([]);
  const [loading, setLoading] = useState(true);

  const sliceImage = useDebouncedCallback(useCallback(() => {
    setVisibleImages((prevImages) =>
      allImages.slice(0, prevImages.length + 1)
    );
  }, [allImages]), 300);

  const checkScrollPosition = useCallback(() => {
    const container = containerRef.current; // Use containerRef.current
    if (!container) return;
    const scrollPosition = container.scrollTop + container.clientHeight; // Updated
    const pageHeight = container.scrollHeight; // Updated

    console.log(scrollPosition + " " + pageHeight);
    if (scrollPosition >= pageHeight - 5) {
      sliceImage();
    }
  }, [sliceImage]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', checkScrollPosition);
    return () => {
      container.removeEventListener('scroll', checkScrollPosition);
    };
  }, [checkScrollPosition]);

  useEffect(() => {
    fetchApi(`/api/Order/Images/${orderIdParam}`)
      .then((response: ViewModel) => {
        setDetails(response);
        setAllImages(response.Images);
        setVisibleImages(response.Images.slice(0, 3)); // Initially display the first 3 images
      }).catch((e) => {
        if (!e?.status)
          alert('Error: Please see admin');
        else if (e.status !== 404) {
          alert(e?.detail);
        }
      }).finally(() => setLoading(false));
  }, [orderIdParam]);

  useEffect(() => {
    const latestChange = visibleImages.find(
      img => (img as OrderImage & { Edited: boolean }).Edited
    );

    if (!latestChange) return;

    setLoading(true);

    // Clear Edited safely
    setVisibleImages(prev =>
      prev.map(img =>
        img.ImageID === latestChange.ImageID
          ? { ...img, Edited: false }
          : img
      )
    );

    const updateImage = async () => {
      try {
        const updateResponse: JsonResponse =
          await fetchApi(`/api/Image/Update/${latestChange.ImageID}`, latestChange, 'PUT');

        if (!updateResponse.Success) {
          alert(updateResponse.ErrorMessage);
          return;
        }

        const refreshed = await fetchApi(`/api/Order/Image/${latestChange.ImageID}`);

        setVisibleImages(prev =>
          prev.map(img =>
            img.ImageID === latestChange.ImageID
              ? { ...refreshed.Image }
              : img
          )
        );
      } catch (e: any) {
        if (!e?.status) {
          alert('Error: Please see admin');
        } else if (e.status !== 404) {
          alert(e.detail);
        }
      } finally {
        setLoading(false);
      }
    };

    updateImage();
  }, [visibleImages]);

  return (
    <div className="mt-3 ml-3" style={{ overflowX: 'auto', height: '97vh' }} ref={containerRef} >
      {
        visibleImages.map((image, index) => (
          <div key={`image-${index}`}>
            <hr />
            <div className="row mb-3">
              <div className="col-8 text-right">
                <img
                  src={`/Image/View/${image.ImageID}`}
                  className="custom-image"
                  onClick={() => openWindowPerc(`/Image/View/${image.ImageID}`, .9, .85, 'ClientApp-Image')}
                  style={{ cursor: 'pointer' }}
                />
              </div>
              <div className="col-4 pl-0" style={{ maxWidth: '300px', minWidth: '275px' }}>
                <div className="row flex-nowrap">
                  <div className="col ml-3 mt-1">
                    <Card>
                      <CardBody style={{ fontSize: '12px' }} className="p-2">
                        <CardSubtitle>
                          <DropDownList
                            disabled={loading}
                            iconClassName="arrow-60-down"
                            svgIcon={caretAltDownIcon}
                            data={details?.PaperworkTypes}
                            value={details?.PaperworkTypes.find(x => x.ID == image.TypeOfPW)}
                            textField="Name"
                            dataItemKey="ID"
                            style={{ fontSize: '12px' }}
                            onChange={(e) => {
                              setVisibleImages(prevImages =>
                                prevImages.map(img =>
                                  img.ImageID === image.ImageID ? { ...img, TypeOfPW: e.value.ID, Edited: true } : img
                                )
                              );
                              setAllImages(prevImages =>
                                prevImages.map(img =>
                                  img.ImageID === image.ImageID ? { ...img, TypeOfPW: e.value.ID } : img
                                )
                              );
                            }}
                          />
                        </CardSubtitle>
                        {/*<CardSubtitle>*/}
                        {/*</CardSubtitle>*/}
                        <label className="mr-1">Print w/Invoice:</label>
                        <Switch
                          disabled={loading}
                          size='small'
                          onChange={(e) => {
                            setVisibleImages(prevImages =>
                              prevImages.map(img =>
                                img.ImageID === image.ImageID ? { ...img, PrintFlag: e.value, Edited: true } : img
                              )
                            );
                            setAllImages(prevImages =>
                              prevImages.map(img =>
                                img.ImageID === image.ImageID ? { ...img, PrintFlag: e.value } : img
                              )
                            );
                          }}
                          checked={image.PrintFlag}
                        />
                        <p className="mb-2">{`Indexed On: ${dayjs.utc(image.ImageDate).format("MM/DD/YYYY")}`}</p>
                        <p className="mb-2">{`Source: ${image.ImageSourceName}`}</p>
                        <p className="mb-2">{`Modified By: ${image.ModifiedByUserNumber}`}</p>
                        <p className="mb-2">{`Modified On: ${dayjs.utc(image.ModifiedDateTime).format("MM/DD/YYYY")}`}</p>
                        <p className="mb-0">{image.ImageID}-{image.PageNum}</p>
                      </CardBody>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))
      }
    </div>
  );
};

export default ImageViewer;